/** @jsx jsx */
import { css, jsx, SerializedStyles } from "@emotion/core"
import React from "react"
import classnames from "classnames"
import { AnimatedChevron } from "./AnimatedChevron"

type TableOpts<T, SortKey = string> = {
  data: T[]
  columns: (
    | false
    | {
        heading?: React.ReactNode
        sortKey?: SortKey
        hidden?: boolean
        narrow?: boolean
        noPadding?: boolean
        tdCss?: (row: T) => SerializedStyles
        render(row: T, i: number): React.ReactNode
      }
  )[]
  sort?: { by: SortKey; direction: "ASC" | "DESC" | "asc" | "desc" }
  onSort?: (sort: { by: SortKey; direction: "ASC" | "DESC" }) => void
  rowClass?: (row: T, i: number) => string | undefined
  rowProps?: (row: T, i: number) => React.HTMLAttributes<HTMLTableRowElement>
  keyExtractor?: (row: T) => string
  css?: SerializedStyles
  additionalRow?: {
    show: (row: T) => boolean
    singleColumn: true
    css?: SerializedStyles
    render(row: T, i: number): React.ReactNode
  }
  compact?: boolean
}
export function renderTable<T, SortKey = string>(opts: TableOpts<T, SortKey>) {
  const columns = opts.columns.filter(Boolean).filter(col => !col.hidden)
  return (
    <table
      css={css(
        css`
          width: 100%;
          border-collapse: collapse;
          text-align: left;

          thead {
            background-color: white;
          }

          th {
            padding: 2px 10px;
            font-weight: 600;
            font-size: 12px;
            color: #444;
            white-space: nowrap;

            &.sortable {
              cursor: pointer;
            }
          }

          tbody {
            tr {
              border: 1px solid #c5c9cd;
              transition: background-color 0.3s;
            }

            td {
              padding: 6px 12px;
              font-size: 13px;
            }
          }

          td.narrow,
          th.narrow {
            width: 0;
          }

          th.noPadding,
          td.noPadding {
            padding: 0 !important;
          }

          th.sortable {
            cursor: pointer;
          }

          ${!opts.compact &&
          css`
            tbody tr {
              height: 50px;
              padding: 8px 10px;
              font-size: 13px;
            }
          `}
        `,
        opts.css,
      )}
    >
      <thead>
        <tr>
          {columns.map((col, i) => (
            <th
              key={i}
              className={classnames({ narrow: col.narrow, sortable: opts.onSort && col.sortKey })}
              onClick={() => {
                if (opts.onSort && col.sortKey) {
                  opts.onSort({
                    by: col.sortKey,
                    direction:
                      opts.sort?.by === col.sortKey
                        ? opts.sort?.direction === "ASC"
                          ? "DESC"
                          : "ASC"
                        : "ASC",
                  })
                }
              }}
            >
              <div
                css={css`
                  display: flex;
                  gap: 8px;
                  align-items: center;
                `}
              >
                {col.heading}
                {opts.sort && col.sortKey ? (
                  <AnimatedChevron
                    dir={
                      opts.sort.by === col.sortKey
                        ? opts.sort.direction.toLowerCase() === "asc"
                          ? 1
                          : -1
                        : 0
                    }
                    size={16}
                  />
                ) : null}
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {opts.data.map((row, i) => {
          const providedRowProps = opts.rowProps?.(row, i)
          const { className: providedClassName, ...restRowProps } = providedRowProps || {}
          return (
            <React.Fragment key={opts.keyExtractor ? opts.keyExtractor(row) : i}>
              <tr
                className={classnames(opts.rowClass?.(row, i), providedClassName)}
                {...restRowProps}
              >
                {columns.map((col, j) => (
                  <td
                    key={j}
                    className={classnames({ narrow: col.narrow, noPadding: col.noPadding })}
                    css={col.tdCss?.(row)}
                  >
                    {col.render(row, i)}
                  </td>
                ))}
              </tr>
              {opts.additionalRow?.show(row) && (
                <tr css={opts.additionalRow.css}>
                  <td colSpan={columns.length}>
                    {opts.additionalRow.singleColumn ? opts.additionalRow.render(row, i) : null}
                  </td>
                </tr>
              )}
            </React.Fragment>
          )
        })}
      </tbody>
    </table>
  )
}
